import { ReactNode, useState } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
} from '@/components/ui/sidebar'
import {
  LayoutDashboard,
  User,
  Shield,
  Settings as SettingsIcon,
  Building2,
  Users,
  Menu,
  LogOut,
  Activity,
  DollarSign,
  AlertTriangle,
  Coins,
  UserCog,
  Cpu,
  Megaphone,
  Brain,
  Map,
  Bot,
  Key,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuth } from '@/contexts/AuthContext'
import { NotificationBell } from './NotificationBell'
import { TierBadge } from './tokens'
import { isSuperAdmin, isAdmin, hasAdminPrivileges } from '@/utils/permissions'
import { ImpersonationBanner } from './ImpersonationBanner'
import { RoleSwitcher } from './RoleSwitcher'
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet'
import { useAccount } from '@/hooks/useAccount'
import { useAgency } from '@/hooks/useAgency'
import { FramedWordmark } from './FramedLogo'

export function DashboardLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, signOut, effectiveRole } = useAuth()
  const [mobileOpen, setMobileOpen] = useState(false)

  // Use effectiveRole for UI visibility (impersonation support)
  const userIsSuperAdmin = isSuperAdmin(user, effectiveRole)
  const userIsAdmin = isAdmin(user, effectiveRole)
  const userHasAdminPrivileges = hasAdminPrivileges(user, effectiveRole)

  // Check if the actual user is super_admin (for features that shouldn't be hidden during impersonation)
  const actualUserIsSuperAdmin = user?.role === 'super_admin'

  // Get account info for terminology
  const { isPersonalAccount, isTeamAccount, displayName: accountDisplayName } = useAccount()
  const { agency, loading: agencyLoading } = useAgency(user?.agency_id)

  // Show "Agency Admin" section only for team accounts with admin privileges
  // Personal accounts don't have team management features
  const showAgencyAdminSection = userIsAdmin && isTeamAccount

  const handleSignOut = async () => {
    // signOut now handles the redirect internally with a full page reload
    await signOut()
  }

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/')
  }

  const closeMobileMenu = () => {
    setMobileOpen(false)
  }

  const SidebarContent_Internal = () => (
    <>
      {/* Header with Logo */}
      <SidebarHeader className="border-b border-framed-neutral-300 p-4">
        <button
          onClick={() => {
            navigate('/dashboard')
            closeMobileMenu()
          }}
          className="hover:opacity-80 transition-opacity w-full flex items-center"
        >
          {!agencyLoading && (
            agency?.logo_url && !actualUserIsSuperAdmin ? (
              <img
                src={agency.logo_url}
                alt={agency.name}
                className="max-w-[140px] max-h-8 object-contain"
              />
            ) : (
              <FramedWordmark className="text-2xl" />
            )
          )}
        </button>
      </SidebarHeader>

      {/* Navigation Menu */}
      <SidebarContent>
        {/* Main Navigation */}
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  onClick={() => {
                    navigate('/dashboard')
                    closeMobileMenu()
                  }}
                  className={cn(
                    'w-full justify-start gap-3 px-3 py-2',
                    isActive('/dashboard') && 'bg-framed-yellow-100 text-framed-yellow-600 font-medium'
                  )}
                >
                  <LayoutDashboard className="w-5 h-5" />
                  <span>Dashboard</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Settings Group */}
        <SidebarGroup>
          <SidebarGroupLabel className="text-xs text-framed-neutral-500 px-3 py-2">Settings</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  onClick={() => {
                    navigate('/settings/profile')
                    closeMobileMenu()
                  }}
                  className={cn(
                    'w-full justify-start gap-3 px-3 py-2',
                    isActive('/settings/profile') && 'bg-framed-yellow-100 text-framed-yellow-600 font-medium'
                  )}
                >
                  <User className="w-5 h-5" />
                  <span>Profile</span>
                </SidebarMenuButton>
              </SidebarMenuItem>

              <SidebarMenuItem>
                <SidebarMenuButton
                  onClick={() => {
                    navigate('/settings/account')
                    closeMobileMenu()
                  }}
                  className={cn(
                    'w-full justify-start gap-3 px-3 py-2',
                    isActive('/settings/account') && 'bg-framed-yellow-100 text-framed-yellow-600 font-medium'
                  )}
                >
                  <Shield className="w-5 h-5" />
                  <span>Account & Security</span>
                </SidebarMenuButton>
              </SidebarMenuItem>

              <SidebarMenuItem>
                <SidebarMenuButton
                  onClick={() => {
                    navigate('/settings/preferences')
                    closeMobileMenu()
                  }}
                  className={cn(
                    'w-full justify-start gap-3 px-3 py-2',
                    isActive('/settings/preferences') && 'bg-framed-yellow-100 text-framed-yellow-600 font-medium'
                  )}
                >
                  <SettingsIcon className="w-5 h-5" />
                  <span>Preferences</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Agency/Account Admin Group - Only for Admins with team accounts */}
        {showAgencyAdminSection && (
          <SidebarGroup>
            <SidebarGroupLabel className="text-xs text-framed-neutral-500 px-3 py-2">
              {accountDisplayName === 'Agency' ? 'Agency Admin' : 'Account Admin'}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    onClick={() => {
                      navigate('/admin/team')
                      closeMobileMenu()
                    }}
                    className={cn(
                      'w-full justify-start gap-3 px-3 py-2',
                      isActive('/admin/team') && 'bg-framed-yellow-100 text-framed-yellow-600 font-medium'
                    )}
                  >
                    <Users className="w-5 h-5" />
                    <span>Team</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>

                <SidebarMenuItem>
                  <SidebarMenuButton
                    onClick={() => {
                      navigate('/admin/broadcasts')
                      closeMobileMenu()
                    }}
                    className={cn(
                      'w-full justify-start gap-3 px-3 py-2',
                      isActive('/admin/broadcasts') && 'bg-framed-yellow-100 text-framed-yellow-600 font-medium'
                    )}
                  >
                    <Megaphone className="w-5 h-5" />
                    <span>Broadcasts</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {/* Super Admin Group - Only for Super Admins */}
        {userIsSuperAdmin && (
          <SidebarGroup>
            <SidebarGroupLabel className="text-xs text-framed-neutral-500 px-3 py-2">Super Admin</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    onClick={() => {
                      navigate('/admin/super-admin')
                      closeMobileMenu()
                    }}
                    className={cn(
                      'w-full justify-start gap-3 px-3 py-2',
                      isActive('/admin/super-admin') && 'bg-framed-yellow-100 text-framed-yellow-600 font-medium'
                    )}
                  >
                    <Activity className="w-5 h-5" />
                    <span>Dashboard</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>

                <SidebarMenuItem>
                  <SidebarMenuButton
                    onClick={() => {
                      navigate('/admin/agency-management')
                      closeMobileMenu()
                    }}
                    className={cn(
                      'w-full justify-start gap-3 px-3 py-2',
                      isActive('/admin/agency-management') && 'bg-framed-yellow-100 text-framed-yellow-600 font-medium'
                    )}
                  >
                    <Building2 className="w-5 h-5" />
                    <span>Agencies</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>

                <SidebarMenuItem>
                  <SidebarMenuButton
                    onClick={() => {
                      navigate('/admin/users')
                      closeMobileMenu()
                    }}
                    className={cn(
                      'w-full justify-start gap-3 px-3 py-2',
                      isActive('/admin/users') && 'bg-framed-yellow-100 text-framed-yellow-600 font-medium'
                    )}
                  >
                    <UserCog className="w-5 h-5" />
                    <span>Users</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>

                <SidebarMenuItem>
                  <SidebarMenuButton
                    onClick={() => {
                      navigate('/admin/cost-monitoring')
                      closeMobileMenu()
                    }}
                    className={cn(
                      'w-full justify-start gap-3 px-3 py-2',
                      isActive('/admin/cost-monitoring') && 'bg-framed-yellow-100 text-framed-yellow-600 font-medium'
                    )}
                  >
                    <DollarSign className="w-5 h-5" />
                    <span>Cost Monitoring</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>

                <SidebarMenuItem>
                  <SidebarMenuButton
                    onClick={() => {
                      navigate('/admin/abuse-monitoring')
                      closeMobileMenu()
                    }}
                    className={cn(
                      'w-full justify-start gap-3 px-3 py-2',
                      isActive('/admin/abuse-monitoring') && 'bg-framed-yellow-100 text-framed-yellow-600 font-medium'
                    )}
                  >
                    <AlertTriangle className="w-5 h-5" />
                    <span>Abuse Monitoring</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>

                <SidebarMenuItem>
                  <SidebarMenuButton
                    onClick={() => {
                      navigate('/admin/tokens/pricing')
                      closeMobileMenu()
                    }}
                    className={cn(
                      'w-full justify-start gap-3 px-3 py-2',
                      isActive('/admin/tokens/pricing') && 'bg-framed-yellow-100 text-framed-yellow-600 font-medium'
                    )}
                  >
                    <Coins className="w-5 h-5" />
                    <span>Token Pricing</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>

                <SidebarMenuItem>
                  <SidebarMenuButton
                    onClick={() => {
                      navigate('/admin/tokens/accounts')
                      closeMobileMenu()
                    }}
                    className={cn(
                      'w-full justify-start gap-3 px-3 py-2',
                      isActive('/admin/tokens/accounts') && 'bg-framed-yellow-100 text-framed-yellow-600 font-medium'
                    )}
                  >
                    <Users className="w-5 h-5" />
                    <span>Token Accounts</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>

                <SidebarMenuItem>
                  <SidebarMenuButton
                    onClick={() => {
                      navigate('/admin/ai-models')
                      closeMobileMenu()
                    }}
                    className={cn(
                      'w-full justify-start gap-3 px-3 py-2',
                      isActive('/admin/ai-models') && 'bg-framed-yellow-100 text-framed-yellow-600 font-medium'
                    )}
                  >
                    <Cpu className="w-5 h-5" />
                    <span>AI Models</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>

                <SidebarMenuItem>
                  <SidebarMenuButton
                    onClick={() => {
                      navigate('/admin/broadcasts')
                      closeMobileMenu()
                    }}
                    className={cn(
                      'w-full justify-start gap-3 px-3 py-2',
                      isActive('/admin/broadcasts') && 'bg-framed-yellow-100 text-framed-yellow-600 font-medium'
                    )}
                  >
                    <Megaphone className="w-5 h-5" />
                    <span>Broadcasts</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>

                <SidebarMenuItem>
                  <SidebarMenuButton
                    onClick={() => {
                      navigate('/admin/knowledge')
                      closeMobileMenu()
                    }}
                    className={cn(
                      'w-full justify-start gap-3 px-3 py-2',
                      isActive('/admin/knowledge') && 'bg-framed-yellow-100 text-framed-yellow-600 font-medium'
                    )}
                  >
                    <Brain className="w-5 h-5" />
                    <span>Knowledge Base</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>

                <SidebarMenuItem>
                  <SidebarMenuButton
                    onClick={() => {
                      navigate('/admin/agents')
                      closeMobileMenu()
                    }}
                    className={cn(
                      'w-full justify-start gap-3 px-3 py-2',
                      isActive('/admin/agents') && 'bg-framed-yellow-100 text-framed-yellow-600 font-medium'
                    )}
                  >
                    <Bot className="w-5 h-5" />
                    <span>Agents</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>

                <SidebarMenuItem>
                  <SidebarMenuButton
                    onClick={() => {
                      navigate('/admin/api-keys')
                      closeMobileMenu()
                    }}
                    className={cn(
                      'w-full justify-start gap-3 px-3 py-2',
                      isActive('/admin/api-keys') && 'bg-framed-yellow-100 text-framed-yellow-600 font-medium'
                    )}
                  >
                    <Key className="w-5 h-5" />
                    <span>API Keys</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>

                <SidebarMenuItem>
                  <SidebarMenuButton
                    onClick={() => {
                      navigate('/processes')
                      closeMobileMenu()
                    }}
                    className={cn(
                      'w-full justify-start gap-3 px-3 py-2',
                      isActive('/processes') && 'bg-framed-yellow-100 text-framed-yellow-600 font-medium'
                    )}
                  >
                    <Map className="w-5 h-5" />
                    <span>Internal Docs</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      {/* User Footer */}
      <SidebarFooter className="border-t border-framed-neutral-300 p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-framed-yellow-100 flex items-center justify-center">
            <User className="w-5 h-5 text-framed-yellow-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-framed-neutral-800 truncate">{user?.name || 'User'}</p>
            <p className="text-xs text-framed-neutral-600 truncate">{user?.email}</p>
          </div>
        </div>
        <button
          onClick={handleSignOut}
          className="flex items-center gap-2 w-full px-3 py-2 text-sm text-framed-neutral-700 hover:bg-framed-neutral-100 rounded-lg transition-colors"
        >
          <LogOut className="w-4 h-4" />
          <span>Log out</span>
        </button>
      </SidebarFooter>
    </>
  )

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-framed-neutral-50">
        {/* Desktop Sidebar */}
        <Sidebar className="hidden lg:flex border-r border-framed-neutral-300">
          <SidebarContent_Internal />
        </Sidebar>

        {/* Mobile Sidebar (Sheet) */}
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetContent side="left" className="p-0 w-64">
            <Sidebar className="border-0">
              <SidebarContent_Internal />
            </Sidebar>
          </SheetContent>
        </Sheet>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Impersonation Banner - shows when super admin is viewing as another role */}
          <ImpersonationBanner />

          {/* Mobile Header */}
          <div className="lg:hidden flex items-center justify-between px-4 py-3 bg-white border-b border-framed-neutral-300">
            <button
              onClick={() => setMobileOpen(true)}
              onMouseEnter={() => setMobileOpen(true)}
              className="p-2 hover:bg-framed-neutral-100 rounded-lg transition-colors"
            >
              <Menu className="w-5 h-5 text-framed-neutral-700" />
            </button>
            <div className="flex items-center gap-3">
              {actualUserIsSuperAdmin && <RoleSwitcher />}
              <TierBadge />
              <NotificationBell />
            </div>
          </div>

          {/* Desktop Header - Optional notifications bar */}
          <div className="hidden lg:flex items-center justify-end gap-4 px-6 py-3 bg-white border-b border-framed-neutral-300">
            {actualUserIsSuperAdmin && <RoleSwitcher />}
            <TierBadge />
            <NotificationBell />
          </div>

          {/* Page Content */}
          <main className="flex-1 overflow-auto">
            <Outlet />
          </main>
        </div>
      </div>
    </SidebarProvider>
  )
}
